/**
 * Attorney Questions — the agent surface for raising a question that needs a licensed
 * attorney's answer (slice 2 of attorney question routing).
 *
 * SHIPS DARK. A submitted question lands on "Waiting to send" and stops there: nothing routes
 * it, emails an attorney, or answers it yet. The page says so plainly, because an agent who
 * believes a member's legal question is on its way when it is not is worse than no page at all.
 *
 * Every rule here is also enforced server-side by create_attorney_question. The client-side
 * checks exist to give a better message, not to be the gate.
 *
 * See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../components/ui/Toast";
import { featureFlags } from "../utils/featureFlags";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  contactPickerCanSearch,
  contactPickerStatus,
} from "../utils/contactPickerStatus";
import { recipientPanelState } from "../utils/attorneyRecipientPanel";
import {
  defaultOwnState,
  isCurrentMember,
  memberRefusalReason,
  memberStatusLabel,
  needsAgentRegionPick,
} from "../utils/attorneyQuestionRules";
import {
  ATTORNEY_TASK_TYPES,
  STAGE_LABELS,
  createAttorneyQuestion,
  listAttorneyJurisdictionChoices,
  listAttorneyQuestions,
  listAttorneyStateRouting,
  previewAttorneyRouting,
  searchAttorneyQuestionMembers,
  unroutableLabel,
  type AttorneyAssignment,
  type AttorneyJurisdictionChoice,
  type AttorneyQuestionMember,
  type AttorneyQuestionRow,
  type AttorneyTaskType,
} from "../services/attorneyQuestions.service";

// Mig 1610 (owner 2026-11-03, reverses 2026-09-20): only CURRENT members may be the subject.
const isMemberSubject = (m: AttorneyQuestionMember) =>
  isCurrentMember(m.member_category);

/** "Wilkes Ellsworth", plus "backup" / "pairing unconfirmed" when they apply. */
function AssignmentLabel({
  name,
  tier,
  subRegion,
  needsReview,
}: {
  name: string | null;
  tier: string | null;
  subRegion: string | null;
  needsReview: boolean | null;
}) {
  return (
    <span>
      <span className="font-medium text-gray-900">{name ?? "—"}</span>
      {subRegion && (
        <span className="ml-1 text-xs text-gray-500">({subRegion})</span>
      )}
      {tier === "backup" && (
        <span className="ml-1 text-xs text-amber-700">backup</span>
      )}
      {needsReview && (
        <span
          className="ml-1 text-xs text-amber-700"
          title="The attorney record flags this pairing as not yet confirmed."
        >
          · pairing unconfirmed
        </span>
      )}
    </span>
  );
}

interface RecipientRow extends AttorneyAssignment {
  routed?: boolean;
  reason?: string | null;
}

/**
 * Recipients grouped by jurisdiction: every attorney (co-counsel included, listed order), an
 * "assign by hand" line for anything unroutable, then the Coverage Questions mailbox CC.
 */
function RecipientList({ rows }: { rows: RecipientRow[] }) {
  const attorneys = rows.filter((r) => (r.kind ?? "attorney") === "attorney");
  // The Coverage Questions mailbox: CC on attorney questions, TO on a coverage question.
  // Mailbox rows: To first (a coverage question), then every CC.
  const mailboxes = rows
    .filter((r) => r.kind === "cc" || r.kind === "to")
    .sort((a, b) => (a.kind === "to" ? 0 : 1) - (b.kind === "to" ? 0 : 1));
  const groups: { code: string; rows: RecipientRow[] }[] = [];
  for (const r of attorneys) {
    const code = r.jurisdiction_code ?? r.state_code ?? "—";
    const g = groups.find((x) => x.code === code);
    if (g) {
      g.rows.push(r);
    } else {
      groups.push({ code, rows: [r] });
    }
  }
  return (
    <ul className="space-y-1" data-testid="aq-recipients">
      {groups.map((g) => (
        <li key={g.code} className="flex gap-2">
          <span className="inline-block w-12 shrink-0 font-mono">{g.code}</span>
          {g.rows.some((r) => r.region_chosen_by_agent) && (
            <span
              className="shrink-0 text-xs text-amber-700"
              data-testid="aq-region-chosen-by-agent"
              title="The member's ZIP could not place them, so the agent chose this region."
            >
              (region chosen by agent)
            </span>
          )}
          {g.rows.some((r) => r.routed === false) ? (
            <span className="text-red-700">
              Cannot be routed automatically —{" "}
              {unroutableLabel(g.rows[0].reason)}. Assign by hand.
            </span>
          ) : (
            <span>
              {g.rows.map((r, i) => (
                <span key={`${g.code}-${i}`}>
                  {i > 0 && <span className="text-gray-400"> + </span>}
                  <AssignmentLabel
                    name={r.attorney_name}
                    tier={r.tier ?? null}
                    subRegion={null}
                    needsReview={r.needs_review ?? null}
                  />
                </span>
              ))}
            </span>
          )}
        </li>
      ))}
      {mailboxes.map((m, i) => (
        <li
          key={`${m.kind}-${m.cc_email ?? "none"}-${i}`}
          className="flex gap-2 text-gray-600"
          data-testid={m.kind === "to" ? "aq-to" : "aq-cc"}
        >
          <span className="inline-block w-12 shrink-0 font-mono">
            {m.kind === "to" ? "To" : "CC"}
          </span>
          <span>
            {m.attorney_name === "Coverage Questions mailbox" && (
              <>Coverage Questions mailbox: </>
            )}
            {m.cc_email ? (
              <span className="text-gray-900">{m.cc_email}</span>
            ) : (
              <span className="text-amber-700">not configured</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The "Who this goes to" body. Never empty: loading, the recipients, or a clear error with a
 * retry (external audit eb927415 P3 — a failed coverage preview used to render a blank panel).
 */
function PreviewBody({
  isLoading,
  error,
  rows,
  onRetry,
}: {
  isLoading: boolean;
  error: unknown;
  rows: RecipientRow[];
  onRetry: () => void;
}) {
  const state = recipientPanelState(isLoading, error, rows.length);
  if (state.kind === "loading") {
    return <span className="text-gray-500">Checking…</span>;
  }
  if (state.kind === "error") {
    return (
      <span className="text-red-700" data-testid="aq-preview-error">
        {state.message}{" "}
        <button
          type="button"
          className="ml-1 text-blue-600 underline"
          onClick={onRetry}
        >
          Retry
        </button>
      </span>
    );
  }
  return <RecipientList rows={rows} />;
}

/**
 * Out-of-state picker: every jurisdiction on the attorney record as a checkbox. Split states
 * appear as their regions. The list comes from the server (the record), not from code here.
 */
function JurisdictionCheckboxes({
  loading,
  error,
  choices,
  selected,
  onToggle,
}: {
  loading: boolean;
  error: Error | null;
  choices: AttorneyJurisdictionChoice[];
  selected: string[];
  onToggle: (code: string) => void;
}) {
  if (loading) {
    return <LoadingSpinner />;
  }
  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error.message}
      </div>
    );
  }
  if (choices.length === 0) {
    return (
      <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
        No attorney is on the record yet, so a question cannot be raised.
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-x-3 gap-y-1 max-h-72 overflow-auto rounded border border-gray-200 p-2"
      data-testid="aq-jurisdictions"
    >
      {choices.map((c) => (
        <label
          key={c.code}
          className={`flex items-center gap-1 text-sm ${
            c.sub_region ? "text-blue-900" : "text-gray-700"
          }`}
          title={
            [c.region_label, c.attorney_names.join(" + ")]
              .filter(Boolean)
              .join(" — ") || undefined
          }
        >
          <input
            type="checkbox"
            data-testid={`aq-jur-${c.code}`}
            checked={selected.includes(c.code)}
            onChange={() => onToggle(c.code)}
          />
          {c.label}
        </label>
      ))}
    </div>
  );
}

/**
 * Dark-flag gate. Returns BEFORE any query is declared, so a flagged-off environment issues
 * no RPCs at all rather than rendering an empty shell that still talks to the database.
 */
function FeatureOffNotice() {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
        <strong className="block text-gray-900 mb-1">Not available yet</strong>
        Attorney question routing is still being built and is not switched on in
        this environment.
      </div>
    </div>
  );
}

const AttorneyQuestionsInner: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLDivElement>(null);

  const [showForm, setShowForm] = useState(false);
  const [contact, setContact] = useState<AttorneyQuestionMember | null>(null);
  const [contactSearch, setContactSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [taskType, setTaskType] =
    useState<AttorneyTaskType>("attorney_required");
  const [states, setStates] = useState<string[]>([]);
  // Mig 1610: Attorney Required in a split state whose region the member's ZIP cannot place — the agent's pick
  // (e.g. "TX-N"). Sent INSTEAD of the state; the server checks it is a real region with an active attorney.
  const [agentRegion, setAgentRegion] = useState<string | null>(null);
  const defaultedFor = useRef<string | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [saving, setSaving] = useState(false);

  const questionsQ = useQuery({
    queryKey: ["attorney-questions"],
    queryFn: () => listAttorneyQuestions(100),
  });
  const routingQ = useQuery({
    queryKey: ["attorney-state-routing"],
    queryFn: listAttorneyStateRouting,
  });
  const {
    data: contactResults = [],
    isFetching: contactSearching,
    isError: contactSearchFailed,
  } = useQuery({
    queryKey: ["attorney-member-search", contactSearch],
    queryFn: () => searchAttorneyQuestionMembers(contactSearch),
    enabled: contactPickerCanSearch(contactSearch),
    // One retry only: a failing search should say so, not spin for ~7 s.
    retry: 1,
  });

  const typeMeta = useMemo(
    () => ATTORNEY_TASK_TYPES.find((t) => t.value === taskType)!,
    [taskType],
  );
  const rows: AttorneyQuestionRow[] = questionsQ.data ?? [];
  const stateRouting = routingQ.data ?? [];
  const coveredStates = stateRouting
    .filter((r) => r.routable)
    .map((r) => r.state_code);
  const byZipStates = new Set(
    stateRouting.filter((r) => r.by_zip).map((r) => r.state_code),
  );
  const unroutableStates = stateRouting.filter((r) => !r.routable);
  const choicesQ = useQuery({
    queryKey: ["attorney-jurisdiction-choices"],
    queryFn: listAttorneyJurisdictionChoices,
  });
  // What is actually submitted: the agent's region pick replaces the state (own-state type only).
  const effectiveStates =
    typeMeta.states === "one" && agentRegion ? [agentRegion] : states;
  const sortedStates = [...effectiveStates].sort();
  // Own-state: does the member's ZIP place them? (Same server resolver as create.) Drives the region picker.
  const basePreviewQ = useQuery({
    queryKey: [
      "attorney-route-preview",
      contact?.id,
      taskType,
      states.join(","),
    ],
    queryFn: () => previewAttorneyRouting(contact!.id, states, taskType),
    enabled: !!contact && typeMeta.states === "one" && states.length === 1,
  });
  const baseAttorneyRow = (basePreviewQ.data ?? []).find(
    (p) => p.recipient_kind === "attorney",
  );
  const regionPickReason =
    typeMeta.states === "one" &&
    baseAttorneyRow &&
    !baseAttorneyRow.routed &&
    needsAgentRegionPick(baseAttorneyRow.reason) &&
    // 1611: only for the member's OWN state (the server refuses anything else: member_not_in_state)
    (contact?.state ?? "").trim().toUpperCase() === states[0]
      ? baseAttorneyRow.reason
      : null;
  const regionChoices = (choicesQ.data ?? []).filter(
    (c) => c.sub_region && c.state_code === states[0],
  );
  const zipRegionCode =
    typeMeta.states === "one" &&
    !agentRegion &&
    baseAttorneyRow?.routed &&
    baseAttorneyRow.jurisdiction_code &&
    baseAttorneyRow.jurisdiction_code !== baseAttorneyRow.state_code
      ? baseAttorneyRow.jurisdiction_code
      : null;
  const regionLabel = (code: string | null) =>
    (code && (choicesQ.data ?? []).find((c) => c.code === code)?.label) || code;
  const previewQ = useQuery({
    queryKey: [
      "attorney-route-preview",
      contact?.id,
      taskType,
      sortedStates.join(","),
    ],
    queryFn: () => previewAttorneyRouting(contact!.id, sortedStates, taskType),
    enabled:
      !!contact &&
      (sortedStates.length > 0 || taskType === "coverage_question"),
  });
  const preview = previewQ.data ?? [];
  const previewRows: RecipientRow[] = preview.map((p) => ({
    kind: p.recipient_kind,
    jurisdiction_code: p.jurisdiction_code,
    attorney_name: p.attorney_name,
    tier: p.tier,
    needs_review: p.needs_review,
    cc_email: p.cc_email,
    routed: p.routed,
    reason: p.reason,
    region_chosen_by_agent:
      p.recipient_kind === "attorney" && p.routed && !!agentRegion,
  }));
  const regionPickPending = !!regionPickReason && !agentRegion;
  const previewBlocked = preview.some((p) => !p.routed);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // The own-state picker uses state codes and the out-of-state picker uses region codes, so a
  // selection never carries across a type change.
  useEffect(() => {
    setStates([]);
    defaultedFor.current = null;
  }, [taskType]);

  // Any change of member, type or state clears an earlier region pick.
  useEffect(() => {
    setAgentRegion(null);
  }, [contact?.id, taskType, states]);

  // Request 1 (2026-11-03): Attorney Required pre-selects the member's own state; the server resolver then shows the
  // matching region and attorney. Once per member+type, so the agent's own change is never overwritten.
  useEffect(() => {
    if (!contact) {
      // Clearing the member clears the state too, so it never carries over to the next member (audit P2-2).
      if (defaultedFor.current !== null) {
        defaultedFor.current = null;
        setStates([]);
      }
      return;
    }
    // Wait for the state list: a failed load must not "use up" the default (audit P3).
    if (taskType !== "attorney_required" || !routingQ.isSuccess) {
      return;
    }
    const key = `${contact.id}|${taskType}`;
    if (defaultedFor.current === key) {
      return;
    }
    defaultedFor.current = key;
    // Always reset: a member with no (or an uncovered) state starts blank, not with the previous member's state.
    const st = defaultOwnState(contact.state, coveredStates);
    setStates(st ? [st] : []);
  }, [contact, taskType, routingQ.isSuccess, coveredStates]);

  const resetForm = () => {
    setShowForm(false);
    setContact(null);
    setContactSearch("");
    setTaskType("attorney_required");
    setStates([]);
    setAgentRegion(null);
    defaultedFor.current = null;
    setQuestionText("");
  };

  const toggleState = (code: string) => {
    setStates((prev) => {
      if (typeMeta.states === "one") {
        return prev[0] === code ? [] : [code];
      }
      return prev.includes(code)
        ? prev.filter((s) => s !== code)
        : [...prev, code];
    });
  };

  const handleSubmit = async () => {
    if (!contact) {
      showToast("error", "Choose the member this question is about.");
      return;
    }
    if (!isMemberSubject(contact)) {
      showToast(
        "error",
        memberRefusalReason(contact.member_category) ??
          "Attorney questions can only be raised for current members.",
      );
      return;
    }
    if (regionPickPending) {
      showToast(
        "error",
        "The member's ZIP does not show which region they are in. Choose the attorney's region first.",
      );
      return;
    }
    if (previewBlocked) {
      showToast(
        "error",
        "One of the chosen states cannot be routed automatically. Remove it and assign that state by hand.",
      );
      return;
    }
    if (!questionText.trim()) {
      showToast("error", "Enter the member's question.");
      return;
    }
    if (typeMeta.states === "one" && states.length !== 1) {
      showToast("error", "Choose exactly one state.");
      return;
    }
    if (typeMeta.states === "many" && states.length < 1) {
      showToast("error", "Choose at least one state.");
      return;
    }
    setSaving(true);
    try {
      const res = await createAttorneyQuestion({
        contact_id: contact.id,
        task_type: taskType,
        question_text: questionText,
        states: effectiveStates,
      });
      showToast("success", `Question #${res.task_number} raised.`);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["attorney-questions"] });
    } catch (e) {
      showToast("error", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const contactLabel = (c: AttorneyQuestionMember) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || c.id;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Attorney Questions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Member questions that need a licensed attorney's answer.
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} variant="primary">
            Raise a question
          </Button>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <strong>Not live yet.</strong> Questions raised here are recorded but
        are not yet sent to an attorney. Keep handling these the way you do
        today — this page is being built and is here for your feedback.
      </div>

      {questionsQ.error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(questionsQ.error as Error).message}
        </div>
      )}

      {showForm && (
        <div className="mb-6 rounded-lg border border-gray-300 bg-white p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Raise a question
          </h2>

          <div className="mb-4" ref={searchRef}>
            <label
              htmlFor="aq-member"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Member
            </label>
            {contact ? (
              <div
                data-testid="aq-member-selected"
                className="flex items-center gap-3 border border-gray-300 rounded-md px-3 py-2 flex-wrap"
              >
                <span className="text-sm">{contactLabel(contact)}</span>
                <span className="text-xs text-gray-400">{contact.email}</span>
                <span className="text-xs text-gray-500">
                  {[contact.membership_id, contact.state, contact.postal_code]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    isMemberSubject(contact)
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  {memberStatusLabel(contact.member_category)}
                </span>
                <button
                  type="button"
                  className="ml-auto text-sm text-blue-600 hover:underline"
                  onClick={() => setContact(null)}
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  id="aq-member"
                  type="text"
                  value={contactSearch}
                  placeholder="Search by name, email, phone or member number…"
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Attorney questions can only be raised for current members.
                </p>
                {showDropdown && contactSearch.trim().length > 0 && (
                  <ul
                    data-testid="aq-member-results"
                    className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow"
                  >
                    {contactResults.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        {contactPickerStatus(
                          contactSearch,
                          contactSearching,
                          contactSearchFailed,
                        )}
                      </li>
                    ) : (
                      contactResults.slice(0, 10).map((c) => {
                        // Not-current contacts stay selectable so the form can SAY why they are refused.
                        const eligible = isMemberSubject(c);
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              data-testid="aq-member-option"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                eligible ? "" : "bg-gray-50 text-gray-500"
                              }`}
                              onClick={() => {
                                setContact(c);
                                setShowDropdown(false);
                              }}
                            >
                              {contactLabel(c)}
                              <span className="ml-2 text-xs text-gray-400">
                                {c.email}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                {[c.membership_id, c.state, c.postal_code]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                              <span
                                className={`ml-2 text-xs ${
                                  eligible ? "text-green-700" : "text-red-600"
                                }`}
                              >
                                {eligible
                                  ? memberStatusLabel(c.member_category)
                                  : `${memberStatusLabel(c.member_category)} — cannot be submitted (current members only)`}
                              </span>
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          {contact && !isMemberSubject(contact) && (
            <div
              data-testid="aq-member-refusal"
              role="alert"
              className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              <strong className="block">
                {memberStatusLabel(contact.member_category)}: a question cannot
                be submitted for this person.
              </strong>
              {memberRefusalReason(contact.member_category)}
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="aq-type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Question type
            </label>
            <select
              id="aq-type"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as AttorneyTaskType)}
              className="w-full md:w-96 border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
            >
              {ATTORNEY_TASK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">{typeMeta.help}</p>
          </div>

          {typeMeta.states !== "none" && (
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                {typeMeta.states === "one"
                  ? "State"
                  : "States and regions involved"}
              </span>
              {typeMeta.states === "many" ? (
                <JurisdictionCheckboxes
                  loading={choicesQ.isLoading}
                  error={choicesQ.error as Error | null}
                  choices={choicesQ.data ?? []}
                  selected={states}
                  onToggle={toggleState}
                />
              ) : routingQ.isLoading ? (
                <LoadingSpinner />
              ) : routingQ.error ? (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {(routingQ.error as Error).message}
                </div>
              ) : coveredStates.length === 0 ? (
                <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  No states have an attorney assigned yet, so a question cannot
                  be raised. Ask an admin to set attorney coverage first.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {coveredStates.map((code) => {
                    const on = states.includes(code);
                    const isDefault =
                      on &&
                      !!contact &&
                      defaultOwnState(contact.state, coveredStates) === code;
                    return (
                      <button
                        key={code}
                        type="button"
                        data-testid={`aq-state-${code}`}
                        title={
                          byZipStates.has(code)
                            ? "Split north/south: the member's ZIP decides the attorney"
                            : undefined
                        }
                        onClick={() => toggleState(code)}
                        className={`px-3 py-1 rounded-full border text-sm ${
                          on
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                        }`}
                      >
                        {code}
                        {byZipStates.has(code) && (
                          <span className="ml-1 text-xs opacity-75">
                            by ZIP
                          </span>
                        )}
                        {isDefault && (
                          <span
                            className="ml-1 text-xs opacity-75"
                            data-testid="aq-state-default"
                          >
                            · member's state
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {typeMeta.states === "many"
                  ? "Split states are listed by region."
                  : "Only states with an attorney on the record can be chosen."}
                {unroutableStates.length > 0 && (
                  <>
                    {" "}
                    Assign by hand (no automatic route):{" "}
                    {unroutableStates
                      .filter(
                        (r) =>
                          typeMeta.states !== "many" ||
                          r.reason !== "needs_county_map",
                      )
                      .map(
                        (r) =>
                          `${r.state_code}${
                            r.reason === "needs_county_map"
                              ? " (split by region, no map yet)"
                              : ""
                          }`,
                      )
                      .join(", ")}
                    .
                  </>
                )}
              </p>
              {contact && states.length > 0 && (
                <div
                  data-testid="aq-route-preview"
                  className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm"
                >
                  <div className="font-medium text-gray-700 mb-1">
                    Who this goes to
                  </div>
                  {zipRegionCode && (
                    <div
                      className="mb-1 text-xs text-gray-600"
                      data-testid="aq-zip-region"
                    >
                      Region: <strong>{regionLabel(zipRegionCode)}</strong>,
                      matched from the member's ZIP
                      {contact.postal_code ? ` (${contact.postal_code})` : ""}.
                    </div>
                  )}
                  {regionPickReason && (
                    <fieldset
                      data-testid="aq-region-picker"
                      className="mb-2 rounded border border-amber-300 bg-amber-50 p-2"
                    >
                      <legend className="px-1 text-xs font-medium text-amber-900">
                        Choose the attorney's region (required)
                      </legend>
                      <p className="mb-1 text-xs text-amber-900">
                        The member&apos;s ZIP
                        {contact.postal_code
                          ? ` (${contact.postal_code})`
                          : " (none on file)"}{" "}
                        does not show which part of {states[0]} they are in.
                        Choose the region to send this question to. Your choice
                        is recorded on the question.
                      </p>
                      {choicesQ.isLoading ? (
                        <LoadingSpinner />
                      ) : regionChoices.length === 0 ? (
                        <span className="text-xs text-red-700">
                          No regions with an attorney are on the record for{" "}
                          {states[0]}. Assign this question by hand.
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {regionChoices.map((c) => (
                            <label
                              key={c.code}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="radio"
                                name="aq-region"
                                data-testid={`aq-region-${c.code}`}
                                checked={agentRegion === c.code}
                                onChange={() => setAgentRegion(c.code)}
                              />
                              <span className="font-medium">{c.label}</span>
                              <span className="text-xs text-gray-600">
                                {c.attorney_names.join(" + ")}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </fieldset>
                  )}
                  {regionPickPending ? (
                    <span
                      className="text-amber-800"
                      data-testid="aq-region-pending"
                    >
                      Choose a region above to see who this goes to.
                    </span>
                  ) : (
                    <PreviewBody
                      isLoading={previewQ.isLoading}
                      error={previewQ.error}
                      rows={previewRows}
                      onRetry={() => previewQ.refetch()}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {typeMeta.states === "none" && contact && (
            <div
              data-testid="aq-route-preview"
              className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm"
            >
              <div className="font-medium text-gray-700 mb-1">
                Who this goes to
              </div>
              <PreviewBody
                isLoading={previewQ.isLoading}
                error={previewQ.error}
                rows={previewRows}
                onRetry={() => previewQ.refetch()}
              />
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="aq-text"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              The member's question
            </label>
            <textarea
              id="aq-text"
              rows={4}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="What did the member ask? Include anything the attorney needs to answer it."
              className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSubmit}
              variant="primary"
              disabled={
                saving ||
                previewBlocked ||
                regionPickPending ||
                (!!contact && !isMemberSubject(contact))
              }
            >
              {saving ? "Submitting…" : "Submit question"}
            </Button>
            <Button onClick={resetForm} variant="secondary" disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {questionsQ.isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          No attorney questions yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  #
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Member
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Question
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  States
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Attorney
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Status
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Raised
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr key={r.support_task_id}>
                  <td className="px-4 py-2 font-medium">{r.task_number}</td>
                  <td className="px-4 py-2">
                    <div>{r.member_name || "—"}</div>
                    <div className="text-xs text-gray-400">
                      {r.membership_id}
                    </div>
                  </td>
                  <td className="px-4 py-2 max-w-md">
                    <div className="truncate" title={r.question_text}>
                      {r.question_text}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.states.length ? r.states.join(", ") : "—"}
                  </td>
                  <td
                    className="px-4 py-2 text-xs min-w-[18rem]"
                    data-testid={`aq-assignments-${r.task_number}`}
                  >
                    {(r.assignments ?? []).length === 0 ? (
                      "—"
                    ) : (
                      <RecipientList rows={r.assignments ?? []} />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                      {STAGE_LABELS[r.stage] ?? r.stage}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {new Date(r.created_at).toLocaleDateString()}
                    {r.created_by_name ? ` · ${r.created_by_name}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AttorneyQuestions: React.FC = () =>
  featureFlags.attorneyQuestionRouting ? (
    <AttorneyQuestionsInner />
  ) : (
    <FeatureOffNotice />
  );

export default AttorneyQuestions;
